import { Button, InputNumber, Spin, Tag, Typography } from "antd";
import {
  ReloadOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import { useRankingToolStore } from "modules/organization/store";
import { FC, KeyboardEvent, useMemo, useState } from "react";
import { renderQuestionType } from "modules/organization/utility/commonFunctions";
import { IPQuestion } from "modules/organization/models/interface";

const ScoreInput: FC<{
  score: number | null;
  onCommit: (score: number) => void;
}> = ({ score, onCommit }) => {
  const [showInvalidMessage, setShowInvalidMessage] = useState(false);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "." || e.key === ",") {
      e.preventDefault();
      setShowInvalidMessage(true);
    }
  };

  return (
    <span className="qstScoreWrap">
      <InputNumber
        className="qstScoreInput"
        size="small"
        precision={0}
        step={1}
        value={score ?? 0}
        aria-label="Score"
        onKeyDown={onKeyDown}
        onChange={(value) => {
          setShowInvalidMessage(false);
          if (value !== null && Number.isInteger(value)) {
            onCommit(value);
          }
        }}
        onBlur={(e) => {
          // FR-007: blank/unparseable input automatically reverts to 0.
          const raw = e.target.value.trim();
          if (raw === "" || Number.isNaN(Number(raw))) {
            onCommit(0);
          }
          setShowInvalidMessage(false);
        }}
      />
      {showInvalidMessage && (
        <span className="qstScoreError" role="alert">
          Enter a whole number.
        </span>
      )}
    </span>
  );
};

const EachQuestion: FC<{
  question: IPQuestion;
  hasChildren: boolean;
  isCollapsed: boolean;
  onToggle: (id: number) => void;
  showVariables: boolean;
  showScoreInput: boolean;
  onScoreChange: (
    questionId: number,
    responseId: number,
    score: number,
  ) => void;
}> = ({
  question,
  hasChildren,
  isCollapsed,
  onToggle,
  showVariables,
  showScoreInput,
  onScoreChange,
}) => {
  return (
    <li className={`depth-${question.depth}`}>
      <div className="qstRowBlk samplingList">
        {hasChildren ? (
          <span
            className="qstExpandToggle"
            role="button"
            aria-label={isCollapsed ? "Expand question" : "Collapse question"}
            onClick={() => onToggle(question.id)}
          >
            {isCollapsed ? (
              <CaretRightOutlined role="img" aria-label="Expand question" />
            ) : (
              <CaretDownOutlined role="img" aria-label="Collapse question" />
            )}
          </span>
        ) : (
          <span className="qstExpandTogglePlaceholder" />
        )}
        <div className="qstIconNumber">
          {renderQuestionType(question.question_type_id)}
          <span>Q{question.question_order_text}</span>
        </div>
        <div className="titleTxt tr">{question.question}</div>
        {showVariables && (
          <span className="qstVariableTag tagWrap">
            <Tag>{question.question_variable}</Tag>
            <Typography.Text copyable={{ text: question.question_variable }} />
          </span>
        )}
      </div>
      <ul>
        {question.responses?.map((response) => (
          <li key={response.response_variable}>
            {response.response}
            {"  "}
            {showVariables && (
              <span className="tagWrap">
                <Tag>{response.response_variable}</Tag>
                <Typography.Text
                  copyable={{ text: response.response_variable }}
                />
              </span>
            )}
            {showScoreInput && (
              <ScoreInput
                score={response.score}
                onCommit={(score) =>
                  onScoreChange(question.id, response.id, score)
                }
              />
            )}
          </li>
        ))}
      </ul>
      {/* GRID ALIGNMENT (commented out) — CSS Grid, tag aligned to the longest response
          text within this question only (not the pane's outer edge).
      <ul className="qstResponseGrid">
        {question.responses?.map((response) => (
          <li key={response.response_variable} className="qstResponseGridRow">
            <span className="qstResponseBullet" aria-hidden="true">•</span>
            <span className="qstResponseText">{response.response}</span>
            <span className="qstVariableTag tagWrap">
              <Tag>{response.response_variable}</Tag>
              <Typography.Text copyable={{ text: response.response_variable }} />
            </span>
          </li>
        ))}
      </ul>
      */}
    </li>
  );
};

const QuestionListContainer: FC<{
  projectSlug?: string;
  rankingProjectId: number;
  rankingTypeId?: number | null;
}> = ({ projectSlug, rankingProjectId, rankingTypeId }) => {
  const {
    questionSnapshotList,
    isLoadingQuestionSnapshot,
    refreshQuestionSnapshot,
    updateResponseScore,
  } = useRankingToolStore();
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());

  // FR-015: Score editing is only available for Point-based Ranking Projects.
  const isPointBased = rankingTypeId === 2;
  const showVariables = !isPointBased;

  const onRefresh = () => {
    if (projectSlug && rankingProjectId && !Number.isNaN(rankingProjectId)) {
      refreshQuestionSnapshot(projectSlug, rankingProjectId);
    }
  };

  const toggleCollapse = (id: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const hasChildrenById = useMemo(() => {
    const list = questionSnapshotList ?? [];
    const map = new Map<number, boolean>();
    list.forEach((question, index) => {
      const next = list[index + 1];
      map.set(question.id, !!next && next.depth > question.depth);
    });
    return map;
  }, [questionSnapshotList]);

  const visibleQuestions = useMemo(() => {
    const list = questionSnapshotList ?? [];
    const visible: IPQuestion[] = [];
    let hideDepthThreshold: number | null = null;
    for (const question of list) {
      if (hideDepthThreshold !== null && question.depth > hideDepthThreshold) {
        continue;
      }
      hideDepthThreshold = null;
      visible.push(question);
      if (collapsedIds.has(question.id) && hasChildrenById.get(question.id)) {
        hideDepthThreshold = question.depth;
      }
    }
    return visible;
  }, [questionSnapshotList, collapsedIds, hasChildrenById]);

  return (
    <div className="qstTreeWrap">
      <div className="qstTreeHeading">
        <span className="qstTreeHeadingTitle">
          Questions
          {(questionSnapshotList?.length ?? 0) > 0 && (
            <span className="qstTreeHeadingCount">
              {questionSnapshotList?.length}
            </span>
          )}
        </span>
        <Button
          type="link"
          size="small"
          className="qstRefreshBtn"
          icon={<ReloadOutlined />}
          onClick={onRefresh}
          disabled={isLoadingQuestionSnapshot}
        >
          Refresh
        </Button>
      </div>
      <Spin spinning={isLoadingQuestionSnapshot}>
        {visibleQuestions.length > 0 ? (
          <ul>
            {visibleQuestions.map((question) => (
              <EachQuestion
                key={question.id}
                question={question}
                hasChildren={!!hasChildrenById.get(question.id)}
                isCollapsed={collapsedIds.has(question.id)}
                onToggle={toggleCollapse}
                showVariables={showVariables}
                showScoreInput={isPointBased}
                onScoreChange={updateResponseScore}
              />
            ))}
          </ul>
        ) : (
          <p>No questions available.</p>
        )}
      </Spin>
    </div>
  );
};

export default QuestionListContainer;
